import React, { useState, useEffect } from 'react';
import HeaderMain from "../components/header/HeaderMain";
import NavMain from "../components/nav/NavMain";
import axios from 'axios';
import assets from "../asset.json";
import liability from "../liability.json";
import equity from "../equity.json";
import income from "../income.json"; // Add income data
import expense from "../expense.json"; // Add expense data
import GLOBALVAR from "../constants"; // Import GLOBALVAR constant
import { Table, Button } from "react-bootstrap";
import ToPeso from '../components/misc/ToPeso';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import LoadingSpinner from '../components/misc/LoadingSpinner';

const jsonDataAssets = assets;
const jsonDataLiability = liability;
const jsonDataEquity = equity;
const jsonDataIncome = income;  // Include income data
const jsonDataExpense = expense; // Include expense data

function TrialBalance() {
    const [dateSelect, setDateSelect] = useState('');
    const [dateHeader, setDateHeader] = useState('');
    const [error, setError] = useState('');
    const [items, setItems] = useState([]);
    const [totalAssets, setTotalAssets] = useState(0);
    const [totalLiabilities, setTotalLiabilities] = useState(0);
    const [totalEquity, setTotalEquity] = useState(0);
    const [totalIncome, setTotalIncome] = useState(0);
    const [totalExpense, setTotalExpense] = useState(0);
    const [totalDebit, setTotalDebit] = useState(0);
    const [totalCredit, setTotalCredit] = useState(0);
    const [showSpinner, setShowSpinner] = useState(false);
    const [balanceTotal, setBalanceTotal] = useState(0);
    const [begBalanceTotal, setBegBalanceTotal] = useState(0);

    // Flatten the JSON data into a single array for all categories (Asset, Liability, Equity, Income, Expense)
    const flattenData = (data, type) => {
        const result = [];
        const traverse = (obj, path = '') => {
            if (Array.isArray(obj)) {
                obj.forEach(item => {
                    result.push({ ...item, type, path: `${path} > ${item.label}` });
                });
            } else if (typeof obj === 'object' && obj !== null) {
                Object.keys(obj).forEach(key => {
                    traverse(obj[key], `${path} > ${key}`);
                });
            }
        };
        traverse(data);
        return result;
    };

    const assetItems = flattenData(jsonDataAssets.Asset, 'Asset');
    const liabilityItems = flattenData(jsonDataLiability.Liability, 'Liability');
    const equityItems = flattenData(jsonDataEquity.Equity, 'Equity');
    const incomeItems = flattenData(jsonDataIncome.Income, 'Income');  // Flatten income data
    const expenseItems = flattenData(jsonDataExpense.Expense, 'Expense');  // Flatten expense data
    const allItems = [...assetItems, ...liabilityItems, ...equityItems, ...incomeItems, ...expenseItems];

    useEffect(() => {
        if (items.length > 0) {
            const assets = items.filter((item) => item.type === "Asset").reduce(
                (acc, curr) => ({
                    balance: acc.balance + parseFloat(curr.balance || 0),
                    begbalance: acc.begbalance + parseFloat(curr.begbalance || 0),
                    credit: acc.credit + parseFloat(curr.credit || 0),
                    debit: acc.debit + parseFloat(curr.debit || 0),
                }),
                { balance: 0, begbalance: 0, credit: 0, debit: 0 } // Correct initial state
            );

            const liabilities = items.filter(item => item.type === 'Liability').reduce(
                (acc, curr) => ({
                    balance: acc.balance + parseFloat(curr.balance || 0),
                    begbalance: acc.begbalance + parseFloat(curr.begbalance || 0),
                    credit: acc.credit + parseFloat(curr.credit || 0),
                    debit: acc.debit + parseFloat(curr.debit || 0),
                }),
                { balance: 0, begbalance: 0, credit: 0, debit: 0 } // Correct initial state
            );
            
            const equity = items.filter(item => item.type === 'Equity').reduce(
                (acc, curr) => ({
                    balance: acc.balance + parseFloat(curr.balance || 0),
                    begbalance: acc.begbalance + parseFloat(curr.begbalance || 0),
                    credit: acc.credit + parseFloat(curr.credit || 0),
                    debit: acc.debit + parseFloat(curr.debit || 0),
                }),
                { balance: 0, begbalance: 0, credit: 0, debit: 0 } // Correct initial state
            );

            const income = items.filter(item => item.type === 'Income').reduce(
                (acc, curr) => ({
                    balance: acc.balance + parseFloat(curr.balance || 0),
                    begbalance: acc.begbalance + parseFloat(curr.begbalance || 0),
                    credit: acc.credit + parseFloat(curr.credit || 0),
                    debit: acc.debit + parseFloat(curr.debit || 0),
                }),
                { balance: 0, begbalance: 0, credit: 0, debit: 0 } // Correct initial state
            );

            const expense = items.filter(item => item.type === 'Expense').reduce(
                (acc, curr) => ({
                    balance: acc.balance + parseFloat(curr.balance || 0),
                    begbalance: acc.begbalance + parseFloat(curr.begbalance || 0),
                    credit: acc.credit + parseFloat(curr.credit || 0),
                    debit: acc.debit + parseFloat(curr.debit || 0),
                }),
                { balance: 0, begbalance: 0, credit: 0, debit: 0 } // Correct initial state
            );


            const begbal = items.reduce((acc, curr) => acc + parseFloat(curr.begbalance || 0), 0);
            const bal = items.reduce((acc, curr) => acc + parseFloat(curr.balance || 0), 0);
            
            const debit = items.reduce((acc, curr) => acc + parseFloat(curr.debit || 0), 0);  // Total Debit
            const credit = items.reduce((acc, curr) => acc + parseFloat(curr.credit || 0), 0);  // Total Credit

            // Ensure the totals are floats

            setTotalAssets(assets);
            setTotalLiabilities(liabilities);
            setTotalEquity(equity);
            setTotalIncome(income);
            setTotalExpense(expense);
            setTotalDebit(parseFloat(debit));
            setTotalCredit(parseFloat(credit));
            setBegBalanceTotal(parseFloat(begbal));
            setBalanceTotal(parseFloat(bal));
        }
    }, [items]);

    const getLastDayLastYear = (dateSelect) => {
        const inputDate = dateSelect; // Your input date
        if (inputDate) {
            const date = new Date(inputDate); // Convert the input string to a Date object

            if (!isNaN(date.getTime())) { // Check if the date is valid
                const previousYear = date.getFullYear() - 1;
                const lastDayOfPreviousYear = new Date(previousYear, 11, 31); // December 31 of the previous year

                // Format the date as YYYY-MM-DD in local time
                const lastDayLastYear = lastDayOfPreviousYear.getFullYear() + '-' +
                    String(lastDayOfPreviousYear.getMonth() + 1).padStart(2, '0') + '-' +
                    String(lastDayOfPreviousYear.getDate()).padStart(2, '0');

                return lastDayLastYear;
            } else {
                console.error("Invalid date input");
            }
        } else {
            console.error("Date input is undefined or empty");
        }

    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setShowSpinner(true);
    
        if (!dateSelect) {
            setError('Select Date is required.');
            setShowSpinner(false);
            return;
        }
    
        try {
            setError('');
    
            const lastDayLastYear = getLastDayLastYear(dateSelect);
    
            // Helper function to group items by type
            const groupByType = (items) => {
                return items.reduce((acc, item) => {
                    const type = item.type.toLowerCase();
                    if (!acc[type]) acc[type] = [];
                    acc[type].push(item);
                    return acc;
                }, {});
            };
    
            // Group allItems by type
            const groupedItems = groupByType(allItems);
    
            // Define endpoints for each type
            const endpoints = {
                asset: {
                    total: `${GLOBALVAR["SERVER_ROOT"]}/api/asset-total/itemdate/multi/trialbal`,
                    type: `${GLOBALVAR["SERVER_ROOT"]}/api/asset-type/itemdate/multi`
                },
                equity: {
                    total: `${GLOBALVAR["SERVER_ROOT"]}/api/equity-total/itemdate/multi/trialbal`,
                    type: `${GLOBALVAR["SERVER_ROOT"]}/api/equity-type/itemdate/multi`
                },
                expense: {
                    total: `${GLOBALVAR["SERVER_ROOT"]}/api/expense-total/itemdate/multi/trialbal`,
                    type: `${GLOBALVAR["SERVER_ROOT"]}/api/expense-type/itemdate/multi`
                },
                income: {
                    total: `${GLOBALVAR["SERVER_ROOT"]}/api/income-total/itemdate/multi/trialbal`,
                    type: `${GLOBALVAR["SERVER_ROOT"]}/api/income-type/itemdate/multi`
                },
                liability: {
                    total: `${GLOBALVAR["SERVER_ROOT"]}/api/liability-total/itemdate/multi/trialbal`,
                    type: `${GLOBALVAR["SERVER_ROOT"]}/api/liability-type/itemdate/multi`
                }
            };
    
            // Function to fetch data for a specific type
            const fetchDataForType = async (type, itemIds) => {
                const responseTotals = await axios.get(endpoints[type].total, {
                    params: {
                        item_ids: itemIds,
                        date: dateSelect,
                    },
                });
    
                const responseBegBalances = await axios.get(endpoints[type].total, {
                    params: {
                        item_ids: itemIds,
                        date: lastDayLastYear,
                    },
                });
    
                const responseTypesCredit = await axios.get(endpoints[type].type, {
                    params: {
                        item_ids: itemIds,
                        date: dateSelect,
                        credit: 'true',
                    },
                });
    
                const responseTypesDebit = await axios.get(endpoints[type].type, {
                    params: {
                        item_ids: itemIds,
                        date: dateSelect,
                        credit: 'false',
                    },
                });
    
                return {
                    totals: responseTotals.data,
                    begBalances: responseBegBalances.data,
                    credits: responseTypesCredit.data,
                    debits: responseTypesDebit.data,
                };
            };
    
            // Process each type and update items
            const updatedItems = [];
            for (const [type, items] of Object.entries(groupedItems)) {
                const itemIds = items.map(item => item.id).join(',');
    
                // Fetch data for the current type
                const { totals, begBalances, credits, debits } = await fetchDataForType(type, itemIds);
                console.log(totals);
                // Update items with fetched data
                const updatedTypeItems = items.map((item) => {
                    const totalEntry = totals.find(entry => entry.item_id === item.id) || {};
                    const begBalanceEntry = begBalances.find(entry => entry.item_id === item.id) || {};
                    const debitEntry = debits.find(entry => entry.item_id === item.id) || {};
                    const creditEntry = credits.find(entry => entry.item_id === item.id) || {};
    
                    return {
                        ...item,
                        begbalance: begBalanceEntry.total || 0,
                        balance: totalEntry.total || 0,
                        debit: debitEntry.totalAmount || 0,
                        credit: creditEntry.totalAmount || 0,
                    };
                });
    
                updatedItems.push(...updatedTypeItems);
            }
    
            // Set the updated items
            setItems(updatedItems);
    
            // Format the date header
            const dateObj = new Date(dateSelect);
            const formattedDate = dateObj.toLocaleDateString('en-US', {
                month: 'long',
                day: '2-digit',
                year: 'numeric'
            });
            setDateHeader(formattedDate);
        } catch (err) {
            console.error("Error fetching data:", err);
            setError('Error fetching transactions. Please try again.');
        } finally {
            setShowSpinner(false);
        }
    };

    const handleExportPDF = async () => {
        setShowSpinner(true);

        try {
            const existingPdfBytes = await fetch("/pdftemplates/trial-balance.pdf").then(res =>
                res.arrayBuffer()
            );

            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
            const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

            const pages = pdfDoc.getPages();
            const firstPage = pages[0];
            const secondPage = pages[1];
            const thirdPage = pages[2];
            const fourthPage = pages[3];
            const fifthPage = pages[4];
            const sixthPage = pages[5];

            const centerText = (text) => {
                const textWidth = helveticaFont.widthOfTextAtSize(text, 9);
                const pageWidth = firstPage.getWidth();
                return (pageWidth - textWidth) / 2;
            };

            const today = new Date();
            const dateTimeString = `${today.toLocaleString('default', { month: 'long' })} ${today.getDate()}, ${today.getFullYear()} ${today.getHours() % 12 || 12}:${today.getMinutes().toString().padStart(2, '0')}:${today.getSeconds().toString().padStart(2, '0')} ${today.getHours() >= 12 ? 'PM' : 'AM'}`;

            firstPage.drawText('As of', { x: centerText('As of'), y: 687, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
            firstPage.drawText(dateHeader, { x: centerText(dateHeader), y: 675, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });

            const coordY = {
                0:183, // Update for specific item positions
                2:221, // CIB-China Bank
                10:342, //Honorarium Loan-Current
                24:525, //Honorarium Loan-Past Due
                38:708, //Honorarium Loan-Litigation
                42:88, //Market Vendors Loan-Litigation
                52:234, //Allowance for Credit Losses on Loans and
                53:270, //Land
                61:380, //Accumulated Depreciation
                68:478, //Due From Head-Office
                69:503, //Accounts Receivable-Cash Advances
                80:688, //Interest Expense-Payable
                81:712, //Accounts Payable-Clients Fund
                85:88, //Accounts Payable-Insurance
                87:125, //Accounts Payable-Mortuary
                93:209, //Dividends Payable
                94:234, //Bills Payable
                95:259, //Withholding Tax Payable-Expanded
                97:295, //Income Tax Payable
                98:320, //Value Added Tax Payable
                99:344, //Other Taxes and Licenses Payable
                100:368, //Unearned Interest and Discount-Loans and
                101:405, //Overages
                102:429, //Advances from Stockholders
                103:454, //Due to Head-Office
                104:478, //Miscellaneous Liabilities
                105:539, //Authorized Stocks
                107:576, //Capital-Others
                108:600, //Deposit for Future Subscription of Capital Stock
                109:626, //Undivided Profits
                110:649, //Retained Earnings
                111:702, //Interest Income-China Bank
                116:112, //Interest Income-Honorarium Loan Current
                130:295, //Interest Income-Honorarium Loan Past Due
                144:478, //Interest Income-Honorarium Loan Li
                158:661, // Service Charge-Honorarium Loan
                166:88, //Service Charge-ALC Employee Loan
                172:198, //Penalty Income-Honorarium Loan Current
                186:393, //Gain/Loss on Disposal of Premises and
                187:429, //Rent Income
                188:454, //Advance Interest
                191:540, //Interest Expense on Financial Liabili
                192:564, //Salaries and Wages
                194:613, //Pag-Ibig ContribuXon-Employer's Share
                197:662, //Pag-Ibig ContribuXon Expense
                200:711, //Taxes and Licenses
                201:735, //Rent Expense
                203:88, //Power, Light and Water
                206:137, //Agency Fee
                223:356, //Depreciation Expense-Building
                229:442, //Bad Debts Written Off
            };

            if (items.length > 0) {
                let coordsFlag = 0;

                items.forEach((item, index) => {
                    const balCon = parseFloat(item.balance);
                    const balance = balCon.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    const begbalance = parseFloat(item.begbalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    const credit = parseFloat(item.credit).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    const debit = parseFloat(item.debit).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

                    if (index <= 41) {
                        coordsFlag = coordY[index] ? 792 - coordY[index] : coordsFlag - 12.30;
                        firstPage.drawText(begbalance, { x: 302, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        firstPage.drawText(debit, { x: 372, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        firstPage.drawText(credit, { x: 442, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        firstPage.drawText(balance, { x: 512, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }
                    
                    if (index > 41 && index <= 84) {
                        coordsFlag = coordY[index] ? 792 - coordY[index] : coordsFlag - 12.30;
                        secondPage.drawText(begbalance, { x: 302, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        secondPage.drawText(debit, { x: 372, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        secondPage.drawText(credit, { x: 442, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        secondPage.drawText(balance, { x: 512, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }

                    if (index > 84 && index <= 115) {
                        coordsFlag = coordY[index] ? 792 - coordY[index] : coordsFlag - 12.30;
                        thirdPage.drawText(begbalance, { x: 302, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        thirdPage.drawText(debit, { x: 372, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        thirdPage.drawText(credit, { x: 442, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        thirdPage.drawText(balance, { x: 512, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }

                    if (index > 115 && index <= 165) {
                        coordsFlag = coordY[index] ? 792 - coordY[index] : coordsFlag - 12.30;
                        fourthPage.drawText(begbalance, { x: 302, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        fourthPage.drawText(debit, { x: 372, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        fourthPage.drawText(credit, { x: 442, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        fourthPage.drawText(balance, { x: 512, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }

                    if (index > 165 && index <= 202) {
                        coordsFlag = coordY[index] ? 792 - coordY[index] : coordsFlag - 12.30;
                        fifthPage.drawText(begbalance, { x: 302, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        fifthPage.drawText(debit, { x: 372, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        fifthPage.drawText(credit, { x: 442, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        fifthPage.drawText(balance, { x: 512, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }

                    if (index > 202 && index <= 229) {
                        coordsFlag = coordY[index] ? 792 - coordY[index] : coordsFlag - 12.30;
                        sixthPage.drawText(begbalance, { x: 302, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        sixthPage.drawText(debit, { x: 372, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        sixthPage.drawText(credit, { x: 442, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                        sixthPage.drawText(balance, { x: 512, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }
                });

                //Assets
                secondPage.drawText(totalAssets.begbalance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 302, y: 142, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                secondPage.drawText(totalAssets.debit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 372, y: 142, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                secondPage.drawText(totalAssets.credit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 442, y: 142, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                secondPage.drawText(totalAssets.balance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 512, y: 142, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });


                //Liabilities
                thirdPage.drawText(totalLiabilities.begbalance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 302, y: 286, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                thirdPage.drawText(totalLiabilities.debit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 372, y: 286, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                thirdPage.drawText(totalLiabilities.credit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 442, y: 286, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                thirdPage.drawText(totalLiabilities.balance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 512, y: 286, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });

                //Equity
                thirdPage.drawText(totalEquity.begbalance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 302, y: 121, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                thirdPage.drawText(totalEquity.debit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 372, y: 121, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                thirdPage.drawText(totalEquity.credit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 442, y: 121, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                thirdPage.drawText(totalEquity.balance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 512, y: 121, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });


                //Income
                fifthPage.drawText(totalIncome.begbalance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 302, y: 290, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                fifthPage.drawText(totalIncome.debit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 372, y: 290, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                fifthPage.drawText(totalIncome.credit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 442, y: 290, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                fifthPage.drawText(totalIncome.balance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 512, y: 290, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });

                //Expense
                sixthPage.drawText(totalExpense.begbalance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 302, y: 323, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                sixthPage.drawText(totalExpense.debit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 372, y: 323, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                sixthPage.drawText(totalExpense.credit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 442, y: 323, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                sixthPage.drawText(totalExpense.balance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 512, y: 323, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });

                

                sixthPage.drawText(dateTimeString, { x: 441, y: 179, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });

                sixthPage.drawText(parseFloat(totalAssets.balance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 166, y: 240, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                sixthPage.drawText(parseFloat(totalLiabilities.balance + totalEquity.balance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 166, y: 227, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                sixthPage.drawText(parseFloat(totalIncome.balance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 166, y: 214, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                sixthPage.drawText(parseFloat(totalExpense.balance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 166, y: 201, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
            }

            const pdfBytes = await pdfDoc.save();
            saveAs(new Blob([pdfBytes]), `Trial_Balance_${Math.floor(new Date(dateHeader).getTime() / 1000)}.pdf`);
        } catch (err) {
            console.error("Error generating PDF:", err);
            setError('Error generating PDF. Please try again.');
        } finally {
            setShowSpinner(false);
        }
    };

    return (
        <>
            {showSpinner && <LoadingSpinner />}
            <NavMain slug="trial-balance" parent="3" />
            <HeaderMain slug="trial-balance" />
            <div className="main-content financials">
                <div className='container'>
                    <h4>Retrieve Trial Balance</h4>
                    <form onSubmit={handleSubmit} className="mb-4">
                        <div className="mb-3">
                            <label htmlFor="dateSelect" className="form-label">Select Date</label>
                            <input
                                type="date"
                                className="form-control"
                                id="dateSelect"
                                value={dateSelect}
                                onChange={(e) => setDateSelect(e.target.value)}
                                required
                            />
                        </div>
                        <button type="submit" className="btn btn-primary">Retrieve</button>
                    </form>
                    {error && <div className="alert alert-danger">{error}</div>}
                    <div className="date-selected">{dateHeader}</div>
                    {items.length > 0 && (
                        <>
                            <Table className="list-container table table-hover" responsive>
                                <thead>
                                    <tr>
                                        <th className='list-header-item'>Type of Account</th>
                                        <th className='list-header-item'>GL Account Description</th>
                                        <th className='list-header-item'>Beginning Balance</th>
                                        <th className='list-header-item'>Debit</th>
                                        <th className='list-header-item'>Credit</th>
                                        <th className='list-header-item'>Ending Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => (
                                        <tr className='list-body' key={index}>
                                            <td className='list-body-item'>{item.type}</td>
                                            <td className='list-body-item'>{item.label}</td>
                                            <td className='list-body-item'><ToPeso amount={item.begbalance} /></td>
                                            <td className='list-body-item'>{item.debit ? <ToPeso amount={item.debit} /> : '-'}</td>
                                            <td className='list-body-item'>{item.credit ? <ToPeso amount={item.credit} /> : '-'}</td>
                                            <td className='list-body-item'><ToPeso amount={item.balance} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className='list-body'>
                                        <td className='list-body-item' colSpan="2">Totals:</td>
                                        <td className='list-body-item'><ToPeso amount={begBalanceTotal} /></td>
                                        <td className='list-body-item'><ToPeso amount={totalDebit} /></td>
                                        <td className='list-body-item'><ToPeso amount={totalCredit} /></td>
                                        <td className='list-body-item'><ToPeso amount={balanceTotal} /></td>
                                    </tr>
                                    <tr className='list-body'>
                                        <td className='list-body-item'>Total Assets:</td>
                                        <td className='list-body-item'><ToPeso amount={totalAssets.balance} /></td>
                                    </tr>
                                    <tr className='list-body'>
                                        <td className='list-body-item'>Total Liabilities and Equity:</td>
                                        <td className='list-body-item'><ToPeso amount={totalLiabilities.balance + totalEquity.balance} /></td>
                                    </tr>
                                    <tr className='list-body'>
                                        <td className='list-body-item'>Gross Income:</td>
                                        <td className='list-body-item'><ToPeso amount={totalIncome.balance} /></td>
                                    </tr>
                                    <tr className='list-body'>
                                        <td className='list-body-item'>Total Expenses:</td>
                                        <td className='list-body-item'><ToPeso amount={totalExpense.balance} /></td>
                                    </tr>
                                </tfoot>
                            </Table>
                            <Button variant="primary" onClick={handleExportPDF}>
                                Export PDF
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default TrialBalance;
